import { useCallback, useEffect, useState } from 'react';
import {
  lineItemHasWork,
  addWireDays,
  formatHours,
  lineItemSchema,
  resourceViewSchema,
  toWireDate,
  wireMonday,
  type Project,
  type ResourceView,
} from '@forecastify/shared';
import { SKELETON_WIDTHS, Skeleton, Toaster, skeletonWidth, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { parseList } from '../permissions-hook';
import type { PickableLineItem } from '../dashboards/line-item-picker';
import { RESOURCE_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { useWorkspaceNavigate } from '../../workspace-path';
import { usePermissionSet } from '../permissions-hook';
import {
  ResourceScreen,
  allocationRefusal,
  type ResourceGrouping,
} from '../dashboards/resource-screen';

/**
 * ⚠️ **U-15 · which way round the tab groups, remembered per browser** — a view choice, like a
 * report's hidden columns, and not something a colleague opening the same project should inherit.
 * Storage that fails falls back to people, the dashboard's own grouping.
 */
const GROUPING_KEY = 'forecastify.projectResource.grouping';

function storedGrouping(): ResourceGrouping {
  try {
    return globalThis.localStorage.getItem(GROUPING_KEY) === 'items' ? 'items' : 'people';
  } catch {
    return 'people';
  }
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The project's Erőforrás tab — C54. The same grid, narrowed.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME COMPONENT AS THE DASHBOARD, NOT A SECOND GRID.** The mockup draws one spreadsheet
 * in two places — `rowFor(uid, pid)` is literally the same function with a project filter — and the
 * cell editor, the three lines, the load colouring and the expand behaviour are the parts anybody
 * would get subtly wrong the second time. What differs is the row set, the figures above it, and
 * which filters make sense; all three are props.
 *
 * ⚠️ **The rows are the project's TEAM, not everybody with hours on it.** The server decides that
 * (see `resource.service.ts`), and the reason is the same one the empty state has: a member with
 * nothing planned yet is precisely who this tab is opened to plan.
 *
 * ⚠️ **Four figures, not six.** *Becslés · Betervezett · Rögzített · Maradék terv* — the project's
 * own question. The workspace strip's *how many people are over-planned* is a resourcing question
 * and belongs on the dashboard, where the rows are everybody.
 */
export function ProjectResourceTab({ project }: { readonly project: Project }) {
  const t = useT();
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment, which is what lets `eslint.config.mjs` ban a bare `go('/people/…')`. */
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();

  /* ⚠️ The planner's window — a week back to five ahead, as the dashboard opens. Computed once,
     because the picker's *Alapértelmezett* resets to it and a value recomputed per render would
     move under somebody at midnight. */
  const [defaultRange] = useState(() => {
    const monday = wireMonday(toWireDate(new Date())) ?? toWireDate(new Date());
    return { from: addWireDays(monday, -7) ?? monday, to: addWireDays(monday, 34) ?? monday };
  });
  const [window, setWindow] = useState(defaultRange);
  const [mode, setMode] = useState<'week' | 'day'>('week');
  const [query, setQuery] = useState('');
  const [grouping, setGrouping] = useState<ResourceGrouping>(storedGrouping);
  const [view, setView] = useState<ResourceView | null>(null);
  const [lineItems, setLineItems] = useState<readonly PickableLineItem[]>([]);
  const [overtimeTypes, setOvertimeTypes] = useState<
    readonly { id: string; name: string; multiplier: string }[]
  >([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const search = new URLSearchParams({
      from: window.from,
      to: window.to,
      mode,
      today: toWireDate(new Date()),
      projectId: project.id,
    });
    /* ⚠️ U-15 · grouped by line item, the search narrows the LINE ITEMS, on the client — sent
       here it would narrow the people and empty every sub-row instead. */
    if (query.trim() !== '' && grouping === 'people') search.set('q', query.trim());

    const response = await session
      .request(`/dashboards/resource?${search.toString()}`)
      .catch(() => null);
    /* ⚠️ A failure leaves the last view rather than throwing a page at somebody who was looking at
       a project — the tab is one of six and the others still work. */
    if (response?.ok !== true) return;
    const parsed = resourceViewSchema.safeParse(await response.json());
    if (parsed.success) setView(parsed.data);
  }, [session, window, mode, query, grouping, project.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * ⚠️⚠️ **THE PICKER OFFERS THIS PROJECT'S LINE ITEMS ONLY.** On the dashboard a planner may put
   * anybody on anything; here the question is *what of THIS project* — and offering another
   * project's deliverable from inside a project page is how somebody plans work onto the wrong
   * contract without noticing.
   */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/line-items').catch(() => null);
      if (response?.ok !== true) return;
      /**
       * ⚠️⚠️ **PARSED BY THE SCHEMA, NOT BY FOUR `typeof` CHECKS AND A CAST.** This hand-rolled
       * its own guard for `id` and `name` and then cast its way to `contractCode`, so the two
       * fields it actually needed were the two nothing validated. `lineItemSchema` is the same
       * shape the rest of the app reads and it carries the estimate and the window the picker
       * shows — CLAUDE.md's *one schema, defined once*.
       */
      const parsed = parseList(lineItemSchema, await response.json());
      if (parsed === null) return;
      setLineItems(
        parsed
          /**
           * ⚠️⚠️ **C55 · A LICENCE IS NOT PLANNABLE, AND `lineItemHasWork` IS THE PREDICATE.**
           * *"Nincs óra és nincs allokáció."* Offering one in the picker would let somebody plan
           * hours against a row that has no estimate to plan against and no task to log to — the
           * cell would accept the number and nothing downstream would ever read it.
           */
          .filter(
            (one) =>
              one.projectId === project.id && one.status !== 'closed' && lineItemHasWork(one.type),
          )
          .map((one) => ({
            id: one.id,
            name: one.name,
            projectName: one.projectName,
            /* R-57 · the contract is named by its identifier on every screen. */
            contractLabel: one.contractIdentifier,
            estimateHours: one.estimateHours,
            startsOn: one.startsOn,
            endsOn: one.endsOn,
          })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId, project.id]);

  /* ⚠️ The row menu's overtime entries — the same list the dashboard reads, and absent rather
     than fatal when the endpoint refuses. */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/overtime/types').catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json()) as { data?: unknown };
      if (!Array.isArray(body.data)) return;
      setOvertimeTypes(
        body.data
          .filter(
            (one): one is { id: string; name: string; multiplier: string; active: boolean } =>
              typeof one === 'object' &&
              one !== null &&
              (one as { active?: unknown }).active === true,
          )
          .map((one) => ({ id: one.id, name: one.name, multiplier: one.multiplier })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId]);

  useLiveInvalidation(RESOURCE_TOPICS, () => {
    void reload();
  });

  /* ⚠️ One writer for the cell, the row patch and the delete — see the dashboard route's note. */
  /* ⚠️ A write that succeeds says so — the same as the dashboard's, and for the same reason:
     the grid redrawing is not an answer, because a live invalidation redraws it too. */
  const write = async (
    path: string,
    init: RequestInit,
    done?: Omit<Toast, 'id'>,
  ): Promise<void> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { code?: string; message?: string }[] };
      } | null;
      setServerError(allocationRefusal(t, body?.error) ?? t('error.unavailable'));
      return;
    }
    await reload();
    if (done !== undefined) {
      setToasts((current) => [
        ...current,
        { ...done, id: `${String(Date.now())}-${String(current.length)}` },
      ]);
    }
  };

  /* First load draws skeleton rows at the grid's own 32px — `skeleton.tsx`. */
  if (view === null) {
    return (
      <div className="flex flex-col">
        {SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
          <div
            key={`skeleton-${String(index)}`}
            aria-hidden
            className="grid h-8 grid-cols-[2fr_1fr_1fr_1fr] items-center gap-3 border-b px-5"
          >
            {[0, 1, 2, 3].map((at) => (
              <Skeleton key={at} width={skeletonWidth(index, at)} />
            ))}
          </div>
        ))}
        <p role="status" aria-live="polite" className="sr-only">
          {t('state.loading')}
        </p>
      </div>
    );
  }

  const totals = view.projectTotals;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {totals === null ? null : (
        <div className="grid grid-cols-2 border-b md:grid-cols-4">
          {[
            { key: 'estimate', value: totals.estimateHours },
            { key: 'planned', value: totals.plannedHours },
            { key: 'tracked', value: totals.trackedHours },
            { key: 'remaining', value: totals.remainingHours },
          ].map((figure) => (
            <div
              key={figure.key}
              className="border-r border-b px-4 py-2.5 last:border-r-0 md:border-b-0"
            >
              <div className="text-muted-foreground truncate text-[11.5px]">
                {t(`project.resource.${figure.key}` as 'project.resource.estimate')}
              </div>
              <div className="mt-0.5 text-[19px] font-medium tabular-nums">
                {formatHours(Number(figure.value))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ResourceScreen
        view={view}
        defaultRange={defaultRange}
        /* ⚠️ **No manager, project or contract filter here** — the tab IS the project filter, and a
           project picker inside a project page is a control that can only make the page wrong. */
        managers={[]}
        projects={[]}
        contracts={[]}
        lineItems={lineItems}
        managerId=""
        projectId=""
        contractId=""
        load=""
        overtimeOnly={false}
        query={query}
        canManage={permissions.has('project.manage')}
        /* ⚠️ Item 29 is a PROJECT question — the workspace grid does not draw it. */
        overview
        grouping={grouping}
        onGrouping={(next) => {
          setGrouping(next);
          try {
            globalThis.localStorage.setItem(GROUPING_KEY, next);
          } catch {
            /* A tab that cannot remember is not a tab that should fail. */
          }
        }}
        serverError={serverError}
        hideStats
        overtimeTypes={overtimeTypes}
        onPatchItem={(item, patch) => {
          const title =
            patch.booking !== undefined
              ? t(patch.booking === 'soft' ? 'resource.toast.soft' : 'resource.toast.hard')
              : patch.distribution !== undefined
                ? t('resource.toast.priority')
                : t(
                    patch.overtimeTypeId === null
                      ? 'resource.toast.overtimeOff'
                      : 'resource.toast.overtimeOn',
                  );
          void write(
            `/allocations/${item.allocationId}`,
            { method: 'PATCH', body: JSON.stringify(patch) },
            { title, description: item.lineItemName },
          );
        }}
        onDeleteItem={(item) => {
          void write(
            `/allocations/${item.allocationId}`,
            { method: 'DELETE' },
            { title: t('resource.toast.removed'), description: item.lineItemName },
          );
        }}
        onManager={() => undefined}
        onProject={() => undefined}
        onContract={() => undefined}
        onLoad={() => undefined}
        onOvertimeOnly={() => undefined}
        onQuery={setQuery}
        onMode={setMode}
        onRange={(from, to) => {
          setWindow({ from, to });
        }}
        onOpenPerson={(memberId) => {
          go(`/people/${memberId}`);
        }}
        onWrite={(input) => {
          void write(
            '/allocations',
            { method: 'POST', body: JSON.stringify(input) },
            { title: t(input.hours > 0 ? 'resource.toast.saved' : 'resource.toast.cleared') },
          );
        }}
        onWriteMany={(writes) => {
          /* ⚠️ One request and one transaction for the whole range — see `saveMany`. */
          const columns = new Set(writes.map((one) => one.fromDate)).size;
          void write(
            '/allocations/batch',
            { method: 'POST', body: JSON.stringify({ writes }) },
            {
              title: t(
                writes.some((one) => one.hours > 0)
                  ? 'resource.toast.copied'
                  : 'resource.toast.clearedRange',
                { count: columns },
              ),
            },
          );
        }}
      />

      <Toaster
        toasts={[...toasts]}
        label={t('resource.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </div>
  );
}
